/**
 * Application sidebar component for NaraHub.
 * Provides navigation and workspace switching.
 * Mobile-first responsive design.
 * Inception floating card design with orange accents.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useProfile } from "../../contexts/ProfileContext";
import { useWorkspaceUrl, publicRoutes } from "../../lib/routes";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { SidebarFooter } from "./SidebarFooter";
import {
  LayoutDashboard,
  FolderKanban,
  Trash2,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Shield,
  Settings,
} from "lucide-react";

/** Truncate text at word boundary, appending ellipsis if needed. */
function truncateAtWord(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  const sub = text.substring(0, maxLen);
  const lastSpace = sub.lastIndexOf(" ");
  return (lastSpace > 0 ? sub.substring(0, lastSpace) : sub) + "\u2026";
}

interface AppSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function AppSidebar({
  collapsed = false,
  onToggle,
  mobileOpen = false,
  onMobileClose,
}: AppSidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { activeWorkspace } = useWorkspace();
  const { isAdmin } = useProfile();
  const urls = useWorkspaceUrl();
  const [workspaceSwitcherOpen, setWorkspaceSwitcherOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Build nav items using workspace URLs
  const navItems = urls
    ? [
        {
          label: t('nav.dashboard'),
          path: urls.dashboard,
          icon: LayoutDashboard,
        },
        {
          label: t('nav.projects'),
          path: urls.projects,
          icon: FolderKanban,
        },
        {
          label: t('nav.trash'),
          path: urls.trash,
          icon: Trash2,
        },
      ]
    : [];

  const workspaceItems =
    activeWorkspace && urls
      ? [
          {
            label: t('nav.collaborators'),
            path: urls.collaborators,
            icon: Users,
          },
        ]
      : [];

  // Logo links to the workspace dashboard
  const dashboardPath = urls?.dashboard || "/";

  return (
    <aside
      className={[
        "fixed top-[18px] left-[18px] bottom-[18px] bg-card rounded-2xl z-50 flex flex-col transition-all duration-300 overflow-y-auto lg:overflow-y-hidden",
        mobileOpen ? "translate-x-0" : "-translate-x-[calc(100%+18px)]",
        "lg:translate-x-0 lg:z-40",
        collapsed ? "sidebar-collapsed" : "sidebar-expanded",
      ].join(" ")}
    >
      {/* Header */}
      <div className="sidebar-header-content flex items-center h-16 px-6">
        <Link
          to={dashboardPath}
          className="sidebar-text flex items-center gap-1.5 transition-colors min-w-0"
          title={activeWorkspace?.name}
        >
          <span className="text-lg font-bold tracking-tight text-foreground whitespace-nowrap">
            {truncateAtWord(activeWorkspace?.name || "NaraHub", 18)}
          </span>
        </Link>
        <button
          onClick={() => setWorkspaceSwitcherOpen(true)}
          className="sidebar-text ml-1 p-1 hover:bg-muted/50 rounded-lg text-muted-foreground shrink-0"
          aria-label="Switch workspace"
        >
          <ChevronsUpDown className="h-4 w-4" />
        </button>
        <button
          onClick={onToggle}
          className="ml-auto p-2 hover:bg-muted/50 rounded-xl text-muted-foreground hidden lg:block shrink-0"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Workspace Switcher Modal */}
      <WorkspaceSwitcher
        open={workspaceSwitcherOpen}
        onOpenChange={setWorkspaceSwitcherOpen}
      />

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`nav-item flex items-center gap-3.5 px-4 py-3 text-[0.9375rem] rounded-xl transition-colors ${
                  isActive(item.path)
                    ? "bg-nara-orange text-nara-charcoal font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="h-[1.2rem] w-[1.2rem] shrink-0" />
                <span className="sidebar-text">
                  {item.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {/* Workspace Section */}
        {workspaceItems.length > 0 && (
          <>
            <div className="section-divider h-px bg-border/50 mx-4 mt-6 mb-4"></div>
            <div className="mb-2 px-3">
              <h3 className="section-label text-xs font-semibold text-muted-foreground/60 uppercase tracking-wider">
                {t('nav.workspaceSection')}
              </h3>
            </div>
            <ul className="space-y-1">
              {workspaceItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-item flex items-center gap-3.5 px-4 py-3 text-[0.9375rem] rounded-xl transition-colors ${
                      isActive(item.path)
                        ? "bg-nara-orange text-nara-charcoal font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-[1.2rem] w-[1.2rem] shrink-0" />
                    <span className="sidebar-text">
                      {item.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      <SidebarFooter
        extraActions={
          <>
            {activeWorkspace && (
              <Link
                to={publicRoutes.workspaceSettings}
                className={`p-2.5 hover:bg-muted/50 rounded-xl transition-colors ${
                  isActive(publicRoutes.workspaceSettings) ? "bg-muted/50" : ""
                }`}
                title={t("workspaceSwitcher.workspaceSettings")}
                aria-label={t("workspaceSwitcher.workspaceSettings")}
              >
                <Settings
                  className={`h-[1.1rem] w-[1.1rem] ${
                    isActive(publicRoutes.workspaceSettings) ? "text-foreground" : "text-muted-foreground"
                  }`}
                />
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin/users"
                className="p-2.5 hover:bg-muted/50 rounded-xl transition-colors"
                title="Admin Hub"
              >
                <Shield className="h-[1.1rem] w-[1.1rem] text-muted-foreground" />
              </Link>
            )}
          </>
        }
      />
    </aside>
  );
}
